// src/services/tables/watchListColumns.js - CANONICAL VERSION
import React from 'react';
import { Eye, Trash2, UserPlus, ArrowLeftRight } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import { formatContractDisplay } from '../contractUtils';
import { renderFloat1, renderFloat2, renderFloat3, renderAvg } from './DynastyTable';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

/**
 * Create dynamic watch list columns based on player type (hitter/pitcher)
 * CANONICAL STRUCTURE: Uses player.ids.mlb, player.info.*, player.stats.*, player.league_contexts[]
 *
 * Column order:
 * - Hitters: POS, Player, Team, G, AB, H, R, HR, RBI, SB, AVG, OPS, Price, Salary, Priority, Added, Actions, Remove
 * - Pitchers: POS, Player, Team, G, GS, IP, W, S, ERA, WHIP, K, QS, Price, Salary, Priority, Added, Actions, Remove
 */
export const createDynamicWatchListColumns = ({
  league = null,
  leagueId = null,
  isPitcher = false,
  onViewPlayer,
  onRemovePlayer,
  onAddPlayer
}) => {
  const columns = [];

  // ==========================================
  // COLUMN 1: Position (yellow text)
  // ==========================================
  columns.push({
    key: 'position',
    title: 'POS',
    width: 45,
    minWidth: 40,
    render: (player, rowType, slotInfo) => (
      <span className={`font-semibold text-sm ${dynastyTheme.classes.text.warning}`}>
        {player.info?.position || '-'}
      </span>
    ),
    sortValue: (player) => player.info?.position || ''
  });

  // ==========================================
  // COLUMN 2: Player Name (no team/position subline)
  // ==========================================
  columns.push({
    key: 'player_name',
    title: 'Player',
    width: 130,
    minWidth: 100,
    render: (player, rowType, slotInfo) => (
      <div className="inline-flex items-center text-left">
        <PlayerLink
          mlbId={player.ids?.mlb}
          name={`${player.info?.first_name || ''} ${player.info?.last_name || ''}`.trim()}
          style={{ fontWeight: 600, fontSize: '0.875rem', color: '#f9fafb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        />
        <InjuryBadge mlbId={player.ids?.mlb} />
      </div>
    ),
    sortValue: (player) => `${player.info?.last_name || ''} ${player.info?.first_name || ''}`.trim(),
    allowOverflow: false
  });

  // ==========================================
  // COLUMN 3: MLB Team
  // ==========================================
  columns.push({
    key: 'mlb_team',
    title: 'Team',
    width: 50,
    minWidth: 45,
    render: (player, rowType, slotInfo) => (
      <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
        {player.info?.mlb_team || 'FA'}
      </span>
    ),
    sortValue: (player) => player.info?.mlb_team || ''
  });

  // ==========================================
  // STAT COLUMNS - Different for hitters vs pitchers
  // ==========================================
  if (!isPitcher) {
    // HITTER STATS: G, AB, H, R, HR, RBI, SB, AVG, OPS
    columns.push(
      {
        key: 'stats.season.hitting.games_played',
        title: 'G',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.games_played || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.games_played || 0
      },
      {
        key: 'stats.season.hitting.at_bats',
        title: 'AB',
        width: 45,
        minWidth: 40,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.at_bats || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.at_bats || 0
      },
      {
        key: 'stats.season.hitting.hits',
        title: 'H',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.hits || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.hits || 0
      },
      {
        key: 'stats.season.hitting.runs',
        title: 'R',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.runs || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.runs || 0
      },
      {
        key: 'stats.season.hitting.home_runs',
        title: 'HR',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.home_runs || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.home_runs || 0
      },
      {
        key: 'stats.season.hitting.rbi',
        title: 'RBI',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.rbi || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.rbi || 0
      },
      {
        key: 'stats.season.hitting.stolen_bases',
        title: 'SB',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.hitting?.stolen_bases || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.stolen_bases || 0
      },
      {
        key: 'stats.season.hitting.batting_avg',
        title: 'AVG',
        width: 50,
        minWidth: 45,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {renderAvg(player.stats?.season?.hitting?.batting_avg)}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.batting_avg || 0
      },
      {
        key: 'stats.season.hitting.ops',
        title: 'OPS',
        width: 55,
        minWidth: 50,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {renderFloat3(player.stats?.season?.hitting?.ops)}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.hitting?.ops || 0
      }
    );
  } else {
    // PITCHER STATS: G, GS, IP, W, S, ERA, WHIP, K, QS
    columns.push(
      {
        key: 'stats.season.pitching.games_played',
        title: 'G',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.games_played || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.games_played || 0
      },
      {
        key: 'stats.season.pitching.games_started',
        title: 'GS',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.games_started || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.games_started || 0
      },
      {
        key: 'stats.season.pitching.innings_pitched',
        title: 'IP',
        width: 50,
        minWidth: 45,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {renderFloat1(player.stats?.season?.pitching?.innings_pitched)}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.innings_pitched || 0
      },
      {
        key: 'stats.season.pitching.wins',
        title: 'W',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.wins || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.wins || 0
      },
      {
        key: 'stats.season.pitching.saves',
        title: 'S',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.saves || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.saves || 0
      },
      {
        key: 'stats.season.pitching.era',
        title: 'ERA',
        width: 50,
        minWidth: 45,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {renderFloat2(player.stats?.season?.pitching?.era)}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.era || 99.99
      },
      {
        key: 'stats.season.pitching.whip',
        title: 'WHIP',
        width: 55,
        minWidth: 50,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {renderFloat2(player.stats?.season?.pitching?.whip)}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.whip || 99.99
      },
      {
        key: 'stats.season.pitching.strikeouts_pitched',
        title: 'K',
        width: 45,
        minWidth: 40,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.strikeouts_pitched || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.strikeouts_pitched || 0
      },
      {
        key: 'stats.season.pitching.quality_starts',
        title: 'QS',
        width: 40,
        minWidth: 35,
        render: (player) => (
          <span className={`${dynastyTheme.classes.text.white} font-mono text-sm`}>
            {player.stats?.season?.pitching?.quality_starts || 0}
          </span>
        ),
        sortValue: (player) => player.stats?.season?.pitching?.quality_starts || 0
      }
    );
  }

  // ==========================================
  // PRICE COLUMN (market price)
  // ==========================================
  columns.push({
    key: 'price',
    title: 'Price',
    width: 50,
    minWidth: 45,
    render: (player) => {
      const price = player.league_context?.financial?.baseline_price;

      return (
        <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
          ${price}
        </span>
      );
    },
    sortValue: (player) => {
      return player.league_context?.financial?.baseline_price;
    }
  });

  // ==========================================
  // SALARY COLUMN (shows "NA" if unowned)
  // ==========================================
  columns.push({
    key: 'salary',
    title: 'Salary',
    width: 55,
    minWidth: 50,
    render: (player) => {
      // Check if player is owned in ANY league the user is involved in
      const isOwnedAnywhere = player.league_contexts?.some(lc =>
        lc.status === 'owned' || lc.status === 'other_team'
      );

      if (!isOwnedAnywhere) {
        return (
          <span className={`${dynastyTheme.classes.text.neutralDark} text-sm`}>
            NA
          </span>
        );
      }

      // Find the salary from owned league contexts
      const ownedContext = player.league_contexts?.find(lc =>
        lc.status === 'owned' || lc.status === 'other_team'
      );
      const salary = ownedContext?.financial?.contract_salary || 0;

      return (
        <span className={`${dynastyTheme.classes.text.success} font-semibold text-sm`}>
          ${salary}
        </span>
      );
    },
    sortValue: (player) => {
      const ownedContext = player.league_contexts?.find(lc =>
        lc.status === 'owned' || lc.status === 'other_team'
      );
      return ownedContext?.financial?.contract_salary || 0;
    }
  });

  // ==========================================
  // OWNERSHIP COLUMNS (if viewing from a specific league)
  // ==========================================
  if (leagueId) {
    columns.push(
      {
        key: 'owner',
        title: 'Owner',
        width: 80,
        minWidth: 70,
        sortable: false,
        render: (player) => {
          const leagueContext = player.league_contexts?.find(lc => lc.league_id === leagueId);
          if (!leagueContext) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;

          return (
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} truncate`}>
              {leagueContext.team?.team_name || 'Free Agent'}
            </div>
          );
        }
      },
      {
        key: 'status',
        title: 'Status',
        width: 60,
        minWidth: 55,
        sortable: false,
        render: (player) => {
          const leagueContext = player.league_contexts?.find(lc => lc.league_id === leagueId);
          if (!leagueContext) return <span className={dynastyTheme.classes.text.neutralDark}>-</span>;

          const isOwned = leagueContext.status === 'owned' || leagueContext.status === 'other_team';
          const displayText = isOwned ? (leagueContext.roster?.status || 'owned') : 'FA';

          return (
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              isOwned
                ? `bg-yellow-400/20 text-yellow-400 border border-yellow-400/30`
                : `bg-green-500/20 text-green-400 border border-green-500/30`
            }`}>
              {displayText}
            </span>
          );
        }
      },
      {
        key: 'contract',
        title: 'Contract',
        width: 55,
        minWidth: 50,
        render: (player) => {
          const leagueContext = player.league_contexts?.find(lc => lc.league_id === leagueId);
          const isOwned = leagueContext?.status === 'owned' || leagueContext?.status === 'other_team';

          if (!leagueContext || !isOwned) {
            return <span className={dynastyTheme.classes.text.neutralDark}>{'\u2014'}</span>;
          }

          const display = formatContractDisplay(leagueContext?.financial?.contract_type, leagueContext?.financial?.contract_years);

          return (
            <span className={`${display === '\u2014' ? dynastyTheme.classes.text.neutralDark : dynastyTheme.classes.text.neutralLight} text-sm`}>
              {display}
            </span>
          );
        },
        sortValue: (player) => {
          const leagueContext = player.league_contexts?.find(lc => lc.league_id === leagueId);
          return leagueContext?.financial?.contract_years || 0;
        }
      }
    );
  }

  // ==========================================
  // METADATA COLUMNS
  // ==========================================
  columns.push(
    {
      key: 'added_at',
      title: 'Added',
      width: 55,
      minWidth: 50,
      render: (player) => {
        const val = player.watch_metadata?.added_at;
        if (!val) return '-';
        const date = new Date(val);
        return (
          <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        );
      },
      sortValue: (player) => new Date(player.watch_metadata?.added_at).getTime()
    },
    {
      key: 'priority',
      title: 'Priority',
      width: 50,
      minWidth: 45,
      render: (player) => (
        <span className={`${dynastyTheme.classes.text.white} font-semibold text-sm`}>
          {player.watch_metadata?.priority || 0}
        </span>
      ),
      sortValue: (player) => player.watch_metadata?.priority || 0
    }
  );

  // ==========================================
  // ACTION COLUMNS
  // ==========================================
  columns.push(
    {
      key: 'actions',
      title: 'Actions',
      width: 50,
      minWidth: 45,
      sortable: false,
      render: (player) => {
        const leagueContext = leagueId ? player.league_contexts?.find(lc => lc.league_id === leagueId) : null;
        const isAvailable = leagueContext?.status === 'available';
        const isOwnedByOther = leagueContext?.status === 'other_team';

        if (!leagueId) {
          return <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>-</span>;
        }

        return (
          <div className="flex items-center gap-1 justify-center">
            {isAvailable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddPlayer(player);
                }}
                className={`p-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.success} hover:bg-green-500/20 transition-colors`}
                title="Add to Team"
              >
                <UserPlus className="w-3.5 h-3.5" />
              </button>
            )}
            {isOwnedByOther && (
              <button
                disabled={true}
                className={`p-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralDark} opacity-50 cursor-not-allowed`}
                title="Trade (Coming Soon)"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
      allowOverflow: true
    },
    {
      key: 'remove',
      title: 'Remove',
      width: 60,
      minWidth: 55,
      sortable: false,
      render: (player) => (
        <div className="flex items-center gap-1 justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewPlayer(player);
            }}
            className={`p-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.info} hover:bg-blue-500/20 transition-colors`}
            title="View Player"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemovePlayer(player.ids?.mlb);
            }}
            className={`p-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.error} hover:bg-red-500/20 transition-colors`}
            title="Remove from Watch List"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
      allowOverflow: true
    }
  );

  return columns;
};

// Kept for backwards compatibility - may be used elsewhere
export const createWatchListColumns = createDynamicWatchListColumns;
